import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, Plus, Trash2, X, Loader2, Shield, Camera } from "lucide-react";
import api from "../../lib/api";
import { Spinner } from "../common";
import AttachmentsPanel from "./AttachmentsPanel";

const CATEGORIES = [
  { k: "medication", l: "Medication" },
  { k: "food", l: "Food" },
  { k: "environmental", l: "Environmental" },
  { k: "insect", l: "Insect / Sting" },
  { k: "other", l: "Other" },
];
const SEVS = [
  { k: "mild",     l: "Mild",     tint: "bg-zinc-100 text-zinc-700" },
  { k: "moderate", l: "Moderate", tint: "bg-amber-100 text-amber-800" },
  { k: "severe",   l: "Severe",   tint: "bg-orange-100 text-orange-800" },
  { k: "critical", l: "Critical", tint: "bg-rose-100 text-rose-800" },
];

function AddDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", category: "medication", severity: "moderate", reaction: "", is_critical: false, note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) return toast.error("Allergy name is required");
    setBusy(true);
    try {
      await api.post("/vault/health/allergies", { ...form, name: form.name.trim() });
      toast.success("Allergy added");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const inp = "mt-1 w-full border border-zinc-300 px-3 py-2 text-sm";
  return (
    <div className="fixed inset-0 z-[9998] bg-zinc-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="allergy-dialog">
      <div className="bg-white border border-zinc-200 shadow-2xl w-full sm:max-w-md sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-600" /><h3 className="font-heading font-800 text-lg">Add allergy</h3></div>
          <button onClick={onClose} data-testid="allergy-close" className="text-zinc-500 hover:text-destructive"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block text-sm"><span className="font-600">Name</span>
            <input autoFocus value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="allergy-name" className={inp} placeholder="e.g. Penicillin, Peanuts, Bee stings" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="font-600">Category</span>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="allergy-category" className={inp}>
                {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.l}</option>)}
              </select>
            </label>
            <label className="block text-sm"><span className="font-600">Severity</span>
              <select value={form.severity} onChange={(e) => set("severity", e.target.value)} data-testid="allergy-severity" className={inp}>
                {SEVS.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
              </select>
            </label>
          </div>
          <label className="block text-sm"><span className="font-600">Reaction <span className="font-400 text-muted-foreground">(optional)</span></span>
            <input value={form.reaction} onChange={(e) => set("reaction", e.target.value)} data-testid="allergy-reaction" className={inp} placeholder="Anaphylaxis, hives, swelling…" />
          </label>
          <label className="block text-sm"><span className="font-600">Notes</span>
            <textarea rows={2} maxLength={400} value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="allergy-note" className={inp} />
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer p-2 border border-rose-200 bg-rose-50">
            <input type="checkbox" checked={form.is_critical} onChange={(e) => set("is_critical", e.target.checked)} data-testid="allergy-critical" className="w-4 h-4 accent-rose-600" />
            <span><Shield className="w-3.5 h-3.5 inline text-rose-600 mr-1" /> Mark as <strong>Critical Emergency Information</strong> — show on emergency card</span>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-200">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="allergy-save" className="px-4 py-2 bg-rose-600 text-white text-sm font-700 inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add allergy
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AllergiesView({ onBack }) {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [openAttach, setOpenAttach] = useState({});
  const load = () => api.get("/vault/health/allergies").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const del = async (a) => {
    if (!window.confirm(`Remove ${a.name} from your allergies?`)) return;
    try { await api.delete(`/vault/health/allergies/${a.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  const toggleAttach = (id) => setOpenAttach((m) => ({ ...m, [id]: !m[id] }));
  return (
    <div data-testid="allergies-view">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} data-testid="allergies-back" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-primary"><ChevronLeft className="w-4 h-4" /> Back</button>
        <button onClick={() => setAdding(true)} data-testid="allergies-add" className="px-3 py-2 bg-rose-600 text-white text-sm font-700 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add allergy</button>
      </div>
      <div className="bg-gradient-to-br from-rose-50 to-white border border-zinc-200 p-4 mb-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-rose-600" />
          <div>
            <h1 className="font-heading font-900 text-2xl">Allergies</h1>
            <p className="text-xs text-muted-foreground">{items?.length || 0} recorded · critical items also appear on your Emergency Card if you opt in</p>
          </div>
        </div>
      </div>
      {items === null ? <Spinner /> : items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-8 text-center" data-testid="allergies-empty">
          <p className="text-sm text-muted-foreground">No allergies recorded yet.</p>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white">
          {items.map((a) => {
            const sev = SEVS.find((s) => s.k === a.severity) || SEVS[1];
            return (
              <div key={a.id} className="px-4 py-3 border-b border-zinc-100 last:border-b-0" data-testid={`allergy-row-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading font-800">
                      {a.name}
                      {a.is_critical && <span className="ml-2 text-[10px] font-900 tracking-wider bg-rose-600 text-white px-1.5 py-0.5 align-middle">CRITICAL</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{a.category} · <span className={`px-1.5 py-0.5 ${sev.tint} text-[10px] font-800`}>{sev.l}</span>{a.reaction ? ` · ${a.reaction}` : ""}</div>
                    {a.note && <div className="text-xs text-zinc-500 mt-0.5">{a.note}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => toggleAttach(a.id)} data-testid={`allergy-attach-${a.id}`} title="Photos" className="text-zinc-400 hover:text-emerald-600 p-1.5"><Camera className="w-4 h-4" /></button>
                    <button onClick={() => del(a)} data-testid={`allergy-delete-${a.id}`} className="text-zinc-400 hover:text-destructive p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {openAttach[a.id] && (
                  <div className="mt-3">
                    <AttachmentsPanel parentType="allergy" parentId={a.id} defaultTags={["allergy", a.category].filter(Boolean)} compact />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {adding && <AddDialog onClose={() => setAdding(false)} onSaved={load} />}
    </div>
  );
}
